import { normalizeOptions } from './utils/options';
import {
  extraSopeCode,
  getAbsolutePath,
  mergeWhat,
  sortGraph
} from './utils/utils';
import { ast, Location, parser, RESERVED_WORDS } from 'peggy';
import { baseTypeMap } from './utils/constants';
import { createVisitor } from './utils/visitor';
import { readFileSync } from 'fs';
import path, { isAbsolute } from 'path';
import translate from './translate';
import { isArray, get } from 'lodash';
import { seal } from './seal';
import { treeshakeImport } from './parser/TreeShake';
import { parserJsCode, CodeGraph } from './parser/ParserJsCode';

export type Options = {
  inputFile: string;
  outputFile: string;
  extensions?: Array<string> | string;
  filter?: Array<string> | string;
  treeshake?: boolean;
};

let ID = 0;

export type Code = string;

export type Grammer = {
  grammer: ast.Grammar;
  code: Code;
};

export enum DependenceWhatType {
  DEFAULT = 'default',
  BINDING = 'binding'
}

export type DependenceWhat = {
  binding: string;
  type: DependenceWhatType;
};

export type DependenceWhatMap = Map<string, Array<DependenceWhatType>>;

export type DependenceParent = {
  index: number;
  import: Array<DependenceWhat>;
  parentPath: string;
};

// 文件引入模块依赖
export type Dependence = {
  id: number;
  path: string; // 当前文件路径
  modulePath: string; // import path
  moduleIndex?: number;
  moduleAbsolutePath: string; // import absolute path
  location: { start: Location; end: Location };
  whatMap: DependenceWhatMap;
  what: Array<DependenceWhat>;
  parent?: Array<DependenceParent>;
};

export type AssertyRuleCode = {
  code: string;
  isBaseType: boolean;
  ruleName: string;
  dependenceRule: Map<string, string>;
};

export interface VisitorRule extends ast.Rule {
  code?: string;
  isBaseType?: boolean;
  dependenceRule?: Map<string, string>;
}

export type AssertNeedCodeMap = Map<string, AssertyRuleCode>;

export type ImportDepNeedCodeMapType = Map<string, AssertNeedCodeMap | null>;

export type Assert = {
  dependencies: Array<Dependence>;
  dependenciesMap: Map<string, number>;
  code: string;
  grammer: ast.Grammar;
  filePath: string; // 原始路径的绝对路径
  path: string; // 原始路径
  parent: Array<DependenceParent> | null;
  mapping?: Map<string, any>;
  ruleCode: Map<string, AssertyRuleCode> | null;
  importDepNeedCodeMap?: ImportDepNeedCodeMapType;
};

export type Graph = Array<Assert>;

type RecursionDependenceReturn =
  | {
      assert: Assert;
      updateQueue: boolean;
    }
  | undefined;

function isBaseType(type: string): boolean {
  if (!type) return false;
  return !!baseTypeMap.has(type);
}
/**
 * @param modulePath 引入的模块路径
 * @param filePath 当前文件的路径
 * @returns 模块的绝对路径
 */
function createModuleAbsolutePath(modulePath: string, filePath: string) {
  if (isAbsolute(modulePath)) return modulePath;
  const dirname = path.dirname(filePath);
  return getAbsolutePath(path.resolve(dirname, modulePath));
}

function createGrammer(code: string, filePath: string): ast.Grammar {
  try {
    const grammer = parser.parse(code, {
      grammarSource: filePath,
      reservedWords: RESERVED_WORDS.slice()
    });
    return grammer;
  } catch (error) {
    throw new Error(`parse file fail: path: ${filePath}, error:${error}`);
  }
}

export function traverse(
  filePath: string,
  code: string,
  options: Record<string, Function>,
  args?: Record<string, any>
): ast.Grammar {
  const grammer = createGrammer(code, filePath);
  createVisitor(grammer, options, args);
  return grammer;
}

function updateDependenceWhat(
  dep: Dependence,
  newWhats: Array<DependenceWhat>
) {
  const what = dep.what;
  const whatMap = dep.whatMap;
  if (!newWhats.length) return false;
  for (let index = 0; index < newWhats.length; index++) {
    const newWhat = newWhats[index];
    const { binding, type } = newWhat;
    if (!whatMap.has(binding)) {
      whatMap.set(binding, [type]);
      what.push(newWhat);
      return;
    }
    diffWhat(newWhat);
  }

  function diffWhat(newWhat: DependenceWhat) {
    const { binding, type } = newWhat;
    const findWhat = whatMap.get(binding)!;
    if (findWhat.indexOf(type) > -1) return false;
    findWhat.push(type);
    what.push(newWhat);
  }
  return dep;
}

function createDependenceWhat(
  what: Array<Record<string, any>> | null
): DependenceWhatMap {
  const whatMap = new Map();
  if (!what) return whatMap;

  for (let index = 0; index < what.length; index++) {
    const { binding, type } = what[index];
    const whatType =
      type === 'import_binding_default'
        ? DependenceWhatType.DEFAULT
        : DependenceWhatType.BINDING;
    if (whatMap.has(binding)) {
      const whatTypes = whatMap.get(binding);
      whatTypes.push(whatType);
      whatMap.set(binding, Array.from(new Set(whatTypes)));
    } else {
      whatMap.set(binding, [whatType]);
    }
  }
  return whatMap;
}

function createAssert(
  assertPath: string | undefined,
  enterFile: string,
  parent: DependenceParent | null,
  args?: Record<string, any>
): Assert {
  if (!assertPath) {
    assertPath = process.cwd();
  }
  const dependencies: Array<Dependence> = [];
  const dependenciesMap: Map<string, number> = new Map();
  const ruleCode: Map<string, AssertyRuleCode> = new Map();
  // const absolutepath = getAbsolutePath(enterFile);
  const absolutepath = createModuleAbsolutePath(enterFile, assertPath);
  const ruleMap = new Map();

  const importFieldMap = new Map();
  const code = readFileSync(absolutepath, { encoding: 'utf-8' });
  const ruleVisitor = {
    initializer,
    grammar_import,
    rule,
    rule_ref,
    action
  };
  const params = {
    ...args,
    content: code
  };
  const grammer = traverse(absolutepath, code, ruleVisitor, params);
  type Initializer = ast.Initializer & {
    codeGraph?: CodeGraph;
  };
  function initializer(source: Initializer, args) {
    const { code } = source;
    // const codeGraph = parserJsCode(code);
    // source.codeGraph = codeGraph;
    // todo... 解析全局js. 创建函数graph
    return source;
  }

  function action(source: ast.Action, args) {
    const { visitor } = args;
    const { code } = source;
    const extraCode = extraSopeCode(code);
    // parserJsCode(extraCode);
    // todo... 解析root rule的js代码。找到依赖的js方法
    visitor(source.expression, args);
    return source;
  }

  // eslint-disable-next-line camelcase
  function rule_ref({ name }: ast.Rule, args) {
    const { rootRuleName, rootRuleDependenceMap } = args;
    if (rootRuleName) {
      rootRuleDependenceMap.set(name, name);
    }
    if (!importFieldMap.has(name)) {
      return;
    }
    importFieldMap.set(name, true);
  }

  // eslint-disable-next-line camelcase
  function grammar_import(source: ast.GrammarImport) {
    const { from, location, what } = source;
    const moduleAbsolutePath = createModuleAbsolutePath(
      from.module,
      absolutepath
    );
    let existIndex: number | undefined = -1;
    const normalizeWhat = what.map(meta => {
      const type =
        meta.type === 'import_binding_default'
          ? DependenceWhatType.DEFAULT
          : DependenceWhatType.BINDING;
      // importFieldMap.set(meta.binding, { path: moduleAbsolutePath });
      importFieldMap.set(meta.binding, false);
      return {
        binding: meta.binding,
        type
      };
    });
    if (
      (existIndex = dependenciesMap.get(moduleAbsolutePath)!) > -1 &&
      existIndex !== undefined
    ) {
      const existDep = dependencies[existIndex];
      updateDependenceWhat(existDep, normalizeWhat);
      return source;
    }
    const dep: Dependence = {
      id: ID++,
      modulePath: from.module,
      moduleAbsolutePath,
      // path: enterFile,
      path: absolutepath,
      location: { start: location.start, end: location.end },
      whatMap: createDependenceWhat(what),
      what: normalizeWhat
    };
    dependencies.push(dep);
    dependenciesMap.set(moduleAbsolutePath, dependencies.length - 1);
  }
  function rule(source: VisitorRule, args) {
    const { content, visitor } = args;
    ruleMap.set(source.name, {});
    const depMap = new Map();
    source.dependenceRule = depMap;
    const { location } = source;
    const code = content.slice(location.start.offset, location.end.offset);
    source.code = code;
    const type = get(source, 'expression.type', '');
    source.isBaseType = isBaseType(type);
    ruleCode.set(source.name, {
      code,
      isBaseType: source.isBaseType,
      ruleName: source.name,
      dependenceRule: depMap
    });
    visitor(source.expression, {
      ...args,
      rootRuleName: source.name,
      rootRuleDependenceMap: depMap
    });

    return source;
  }

  const { deps, depMap } = treeshakeImport(importFieldMap, dependencies);
  return {
    dependencies: deps,
    dependenciesMap: depMap,
    code,
    grammer, // grammer中的imports没有tree shake. 请使用dependencies
    parent: parent ? [parent] : null,
    filePath: absolutepath,
    path: enterFile,
    ruleCode
  };
}

export type QueueInfo = {
  index: number; // assert在queue中的索引
};

function createGraph(enterFile: string): Graph {
  const mainAsserts = createAssert(undefined, enterFile, null);
  const graphQueue = [mainAsserts];
  const queueMap: Map<string, QueueInfo> = new Map();
  queueMap.set(mainAsserts.filePath, { index: 0 });
  function recursionDependence(
    dependence: Dependence,
    assert: Assert,
    index: number
  ): RecursionDependenceReturn | undefined {
    let updateQueue = true;
    const depPath = createModuleAbsolutePath(
      dependence.modulePath,
      dependence.path
    );
    let existGraphInfo;
    if ((existGraphInfo = queueMap.get(depPath))) {
      // 存在graph
      const findAssert = graphQueue[existGraphInfo.index];
      if (!findAssert.parent) {
        findAssert.parent = [];
      }
      findAssert.parent.push({
        index,
        parentPath: assert.filePath,
        import: dependence.what,
        [depPath]: dependence.what
      });
      updateQueue = false;
    }
    if (!assert.mapping) {
      assert.mapping = new Map();
    }
    if (assert.mapping.has(depPath)) {
      const moduleInfo = assert.mapping.get(depPath);
      if (!moduleInfo) return undefined;
      const mWhat = moduleInfo.what;
      if (!isArray(mWhat)) {
        moduleInfo.what = [];
      }
      if (!dependence.what || !dependence.what.length) return undefined;
      moduleInfo.what = mergeWhat(mWhat, dependence.what);
      return undefined;
    }
    assert.mapping.set(depPath, {
      modulePath: dependence.modulePath,
      what: dependence.what
    });
    const childAssert = createAssert(assert.filePath, dependence.modulePath, {
      index,
      import: dependence.what,
      parentPath: graphQueue[index].filePath,
      //   [dependence.modulePath]: dependence.what,
      [depPath]: dependence.what
    });
    return { assert: childAssert, updateQueue };
  }
  for (let index = 0; index < graphQueue.length; index++) {
    const assert = graphQueue[index];
    const dependenceAssert: Array<RecursionDependenceReturn> = [];
    assert.dependencies.forEach(dependence => {
      const depAssert = recursionDependence(dependence, assert, index);
      if (depAssert) {
        dependenceAssert.push(depAssert);
      }
    });
    if (!dependenceAssert.length) continue;
    for (let index = 0; index < dependenceAssert.length; index++) {
      const assertInfo = dependenceAssert[index];
      const { assert: depAssert, updateQueue } = assertInfo!;
      updateParentInfo(depAssert, graphQueue.length - 1, graphQueue);
      if (updateQueue) {
        graphQueue.push(depAssert);
        queueMap.set(depAssert.filePath, { index: graphQueue.length - 1 });
      }
    }
  }
  return graphQueue;
}

function updateParentInfo(
  assert: Assert,
  curIndex: number,
  graphQueue: Array<Assert>
) {
  if (!assert.parent) return false;
  const parents: Array<DependenceParent> = assert.parent;
  if (!isArray(parents) || !parents.length) return;
  for (let index = 0; index < parents.length; index++) {
    const parentInfo = parents[index];
    const parentAssert = graphQueue[parentInfo.index];
    if (!parentAssert) {
      console.warn(`The parent node was not found,curPath:${assert.filePath}`);
    }
    const parentDep = parentAssert.dependencies;
    for (let index = 0; index < parentDep.length; index++) {
      const dep = parentDep[index];
      if (dep.moduleAbsolutePath === assert.filePath) {
        dep.moduleIndex = curIndex;
      }
    }
  }
}

function pffe(enterOptions: Options) {
  const options = normalizeOptions(enterOptions);
  const { inputFile } = options;
  const graph = createGraph(inputFile);
  const bundle = translate(sortGraph(graph));
  seal(bundle, options);
  console.log('executed successfully!!');
}

// export default pffe;
module.exports = pffe;
